import L from 'leaflet'
import { useEffect, useRef } from 'react'
import type { MapMeta, PublicSpot } from '../lib/types'
import { spotDisplayName } from '../lib/spotLabel'

type Props = {
  spots: PublicSpot[]
  meta: MapMeta
  mode: 'view' | 'single' | 'multi'
  // ids of polygons that already belong to me — rendered as "mine".
  mySpotIds?: string[]
  // ids currently selected (not yet confirmed). Single = at most 1.
  selectedIds?: string[]
  // ids visually highlighted as "recommended" (for multi-mode neighbour hint).
  recommendedIds?: string[]
  onToggle?: (spotId: string) => void
  // When true, spots taken by someone else are also clickable (they fire
  // onToggle) — used by the toewijs-werkbank to inspect a spot's occupant.
  allowTakenClicks?: boolean
  height?: number
}

const COLORS = {
  free: '#e5ff73',         // lime
  parkFree: '#7fb3ff',     // blue — vrij maar auto-vrije zone (Fluweelpark)
  taken: '#5a5a5a',        // grey
  mine: '#A077F3',         // purple
  selected: '#cf2e2e',     // red
  recommended: '#ff8800',  // vivid orange — clearly distinct from vrij/jouw/geselecteerd
}

export default function MapPicker({
  spots,
  meta,
  mode,
  mySpotIds = [],
  selectedIds = [],
  recommendedIds = [],
  onToggle,
  allowTakenClicks = false,
  height = 420,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layersRef = useRef<Map<string, L.Polygon>>(new Map())
  // Signature of the polygon-id set; the map only re-fits its bounds when this
  // changes (or on first render), never on selection/recommendation changes —
  // otherwise every spot click would snap the view back and discard zoom/pan.
  const fitSigRef = useRef<string | null>(null)

  const onToggleRef = useRef(onToggle)
  onToggleRef.current = onToggle

  const readOnly = mode === 'view'

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, {
      center: [meta.center[1], meta.center[0]],
      zoom: meta.zoom,
      scrollWheelZoom: true,
    })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 22,
    }).addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      layersRef.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const mineSet = new Set(mySpotIds)
    const selSet = new Set(selectedIds)
    const recSet = new Set(recommendedIds)

    for (const [id, layer] of layersRef.current) {
      if (!spots.find((s) => s.id === id)) {
        layer.remove()
        layersRef.current.delete(id)
      }
    }

    for (const spot of spots) {
      const latlngs = spot.coordinates.map(([lng, lat]) => [lat, lng] as [number, number])
      const isMine = mineSet.has(spot.id)
      const isSelected = selSet.has(spot.id)
      const isRecommended = recSet.has(spot.id) && !spot.taken && !isMine
      const fill = isSelected
        ? COLORS.selected
        : isMine
          ? COLORS.mine
          : spot.taken
            ? COLORS.taken
            : isRecommended
              ? COLORS.recommended
              : spot.carFree
                ? COLORS.parkFree
                : COLORS.free
      const opacity = spot.taken && !isMine ? 0.55 : 0.85
      const clickable = !readOnly && (!spot.taken || isMine || allowTakenClicks)
      const cursor = clickable ? 'cursor: pointer;' : ''

      let layer = layersRef.current.get(spot.id)
      if (!layer) {
        layer = L.polygon(latlngs, {
          color: '#000000',
          fillColor: fill,
          fillOpacity: opacity,
          weight: 3,
        }).addTo(map)
        layer.bindTooltip(spotDisplayName(spot), { sticky: true })
        layersRef.current.set(spot.id, layer)
      } else {
        layer.setLatLngs(latlngs)
        layer.setStyle({ color: '#000000', fillColor: fill, fillOpacity: opacity, weight: 3 })
      }

      const el = (layer as any).getElement?.() as SVGElement | undefined
      if (el) el.setAttribute('style', cursor)

      layer.off('click')
      if (clickable) {
        layer.on('click', () => onToggleRef.current?.(spot.id))
      }
    }

    const sig = `${spots.map((s) => s.id).join('|')}#${mySpotIds.join('|')}`
    if (spots.length > 0 && sig !== fitSigRef.current) {
      const bounds = L.latLngBounds([])
      for (const layer of layersRef.current.values()) bounds.extend(layer.getBounds())
      if (bounds.isValid()) {
        // In view-modus zoomen we in op de eigen plek: standhouders openen hun
        // magic link en willen meteen zien waar ze staan, niet de hele markt.
        // Drie niveaus dieper dan het overzicht — relatief, zodat het blijft
        // kloppen als er plekken bij komen. In kies-modus niet, daar moet je
        // de vrije plekken rondom kunnen zien.
        const mine = L.latLngBounds([])
        if (readOnly) {
          for (const id of mySpotIds) {
            const layer = layersRef.current.get(id)
            if (layer) mine.extend(layer.getBounds())
          }
        }
        if (mine.isValid()) {
          const overview = map.getBoundsZoom(bounds, false, L.point(40, 40))
          map.setView(mine.getCenter(), Math.min(overview + 3, map.getMaxZoom()))
        } else {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: meta.zoom + 1 })
        }
        fitSigRef.current = sig
      }
    }
  }, [spots, mySpotIds, selectedIds, recommendedIds, mode, meta.zoom, allowTakenClicks])

  return (
    <div
      ref={containerRef}
      className="rounded-chunky overflow-hidden border border-line"
      style={{ height }}
    />
  )
}
